import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import ProgramForm from '../../components/cultivar/ProgramForm';
import {
  Award, Plus, Pencil, Trash2, Loader2, ArrowLeft, ShieldAlert, Search, X,
  Building2, DollarSign, CalendarClock, CheckCircle2, PauseCircle, Leaf, UserPlus,
  History, FileText, ShieldCheck,
} from 'lucide-react';
import type { Cultivar } from '../../types/cultivar';
import cultivarService from '../../services/cultivar';
import {
  PROGRAM_TYPE_LABELS,
  ROYALTY_UNIT_LABELS,
  REPORTING_CADENCE_LABELS,
  type CultivarProgram,
  type ProgramFormData,
} from '../../types/cultivar';
import { useAuth } from '../../context/AuthContext';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { toNumOr } from '@/utils/numeric';

function formatRoyalty(p: CultivarProgram): string {
  if (p.royalty_rate == null || p.royalty_rate === '') return '—';
  const unit = p.royalty_unit ? ` ${ROYALTY_UNIT_LABELS[p.royalty_unit].toLowerCase()}` : '';
  if (p.royalty_unit === 'percent_of_sale') {
    return `${Number(p.royalty_rate)}% of sale`;
  }
  return `${p.currency || 'AUD'} ${Number(p.royalty_rate).toFixed(2)}${unit}`;
}

function formatDueDate(p: CultivarProgram): string {
  if (!p.reporting_due_month && !p.reporting_due_day) return '';
  const monthNames = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];
  const parts: string[] = [];
  if (p.reporting_due_day) parts.push(String(p.reporting_due_day));
  if (p.reporting_due_month) parts.push(monthNames[p.reporting_due_month - 1]);
  return parts.join(' ');
}

function ProgramsAdmin() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasAccess: canManage, loading: licenseLoading } = useModuleLicense('cultivar', 'admin');

  const [programs, setPrograms] = useState<CultivarProgram[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<CultivarProgram | null>(null);
  const [creating, setCreating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<CultivarProgram | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Manage cultivars modal
  const [managingProgram, setManagingProgram] = useState<CultivarProgram | null>(null);
  const [assignedCultivars, setAssignedCultivars] = useState<Cultivar[]>([]);
  const [loadingAssigned, setLoadingAssigned] = useState(false);
  const [cultivarSearch, setCultivarSearch] = useState('');
  const [cultivarResults, setCultivarResults] = useState<Cultivar[]>([]);
  const [searchingCultivars, setSearchingCultivars] = useState(false);
  const [showCultivarDropdown, setShowCultivarDropdown] = useState(false);
  const [assigningId, setAssigningId] = useState<number | null>(null);
  const [removingId, setRemovingId] = useState<number | null>(null);
  const cultivarSearchRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await cultivarService.listPrograms({ mine: true });
      if (response.success && response.data) {
        setPrograms(response.data);
      }
    } catch (err) {
      console.error('Failed to load programs:', err);
      setError('Failed to load programs.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canManage) load();
  }, [canManage, load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return programs;
    return programs.filter((p) =>
      [p.name, p.organisation_name, p.description, p.program_type]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [programs, search]);

  const handleCreate = async (data: ProgramFormData) => {
    setSubmitting(true);
    try {
      const response = await cultivarService.createProgram(data);
      if (response.success) {
        setCreating(false);
        await load();
      } else {
        setError(response.message || 'Failed to create program.');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to create program.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (data: ProgramFormData) => {
    if (!editing) return;
    setSubmitting(true);
    try {
      const response = await cultivarService.updateProgram(editing.id, data);
      if (response.success) {
        setEditing(null);
        await load();
      } else {
        setError(response.message || 'Failed to update program.');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to update program.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSubmitting(true);
    try {
      const response = await cultivarService.deleteProgram(deleteTarget.id);
      if (response.success) {
        setDeleteTarget(null);
        await load();
      } else {
        setError(response.message || 'Failed to delete program.');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to delete program.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const openManageModal = async (program: CultivarProgram) => {
    setManagingProgram(program);
    setCultivarSearch('');
    setCultivarResults([]);
    setShowCultivarDropdown(false);
    setLoadingAssigned(true);
    try {
      const res = await cultivarService.searchCultivars({ program_id: program.id, limit: 100 });
      if (res.success && res.data) setAssignedCultivars(res.data.cultivars);
    } catch {
      // ignore — logged by interceptor
    } finally {
      setLoadingAssigned(false);
    }
  };

  const closeManageModal = () => {
    setManagingProgram(null);
    setAssignedCultivars([]);
    setCultivarSearch('');
    setCultivarResults([]);
  };

  const searchCultivarsForAssign = async (query: string) => {
    if (query.trim().length < 2) {
      setCultivarResults([]);
      return;
    }
    setSearchingCultivars(true);
    try {
      const res = await cultivarService.searchCultivars({ search: query, limit: 20 });
      if (res.success && res.data) {
        const assignedIds = new Set(assignedCultivars.map((c) => c.id));
        setCultivarResults(res.data.cultivars.filter((c) => !assignedIds.has(c.id)));
      }
    } catch {
      // ignore
    } finally {
      setSearchingCultivars(false);
    }
  };

  const handleAssignCultivar = async (cultivar: Cultivar) => {
    if (!managingProgram) return;
    setAssigningId(cultivar.id);
    try {
      const res = await cultivarService.addCultivarProgram(cultivar.id, managingProgram.id);
      if (res.success) {
        setAssignedCultivars((prev) => [...prev, cultivar]);
        setCultivarResults((prev) => prev.filter((c) => c.id !== cultivar.id));
        setCultivarSearch('');
        setShowCultivarDropdown(false);
        setPrograms((prev) =>
          prev.map((p) =>
            p.id === managingProgram.id
              ? { ...p, cultivar_count: toNumOr(p.cultivar_count) + 1 }
              : p
          )
        );
      }
    } catch {
      // ignore — logged by interceptor
    } finally {
      setAssigningId(null);
    }
  };

  const handleRemoveCultivar = async (cultivar: Cultivar) => {
    if (!managingProgram) return;
    setRemovingId(cultivar.id);
    try {
      const res = await cultivarService.removeCultivarProgram(cultivar.id, managingProgram.id);
      if (res.success) {
        setAssignedCultivars((prev) => prev.filter((c) => c.id !== cultivar.id));
        setPrograms((prev) =>
          prev.map((p) =>
            p.id === managingProgram.id
              ? { ...p, cultivar_count: Math.max(0, toNumOr(p.cultivar_count, 1) - 1) }
              : p
          )
        );
      }
    } catch {
      // ignore — logged by interceptor
    } finally {
      setRemovingId(null);
    }
  };

  if (licenseLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader title="Cultivar Programs" subtitle="Manage royalty and licensing programs" icon={<Award className="h-6 w-6 text-white" />} />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 text-center text-neutral-600">Loading…</div>
      </div>
    );
  }

  if (!canManage) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="Cultivar Programs"
          subtitle="Manage royalty and licensing programs"
          icon={<Award className="h-6 w-6 text-white" />}
        />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
          <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-8 text-center">
            <div className="h-12 w-12 rounded-full bg-error-100 flex items-center justify-center mx-auto mb-4">
              <ShieldAlert className="h-6 w-6 text-error-600" />
            </div>
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">Cultivar Admin licence required</h3>
            <p className="text-neutral-600 mb-6">
              Managing cultivar programs requires a Cultivar Admin licence seat.
            </p>
            <Button variant="outline" onClick={() => navigate('/cultivar')}>
              <ArrowLeft className="h-4 w-4" />
              Back to Cultivar Registry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Cultivar Programs"
        subtitle="Royalty and licensing programs that govern cultivar use"
        icon={<Award className="h-6 w-6 text-white" />}
        actionButtons={
          <>
            <Button variant="outline" onClick={() => navigate('/cultivar')}>
              <ArrowLeft className="h-4 w-4" />
              Back to Registry
            </Button>
            <Button variant="primary" onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" />
              New Program
            </Button>
          </>
        }
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        {error && (
          <div className="rounded-lg p-4 bg-error-50 border border-error-200 flex items-start justify-between">
            <p className="text-sm text-error-800">{error}</p>
            <button onClick={() => setError(null)} className="text-error-500 hover:text-error-700">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        <div className="relative max-w-md">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search programs..."
            className="input-professional w-full pl-10"
          />
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          </div>
        ) : filtered.length === 0 ? (
          <Card>
            <CardContent className="p-10 text-center">
              <Award className="h-10 w-10 mx-auto text-neutral-300 mb-3" />
              <h3 className="text-lg font-semibold text-neutral-900 mb-1">
                {programs.length === 0 ? 'No programs yet' : 'No matches'}
              </h3>
              <p className="text-sm text-neutral-500 mb-4">
                {programs.length === 0
                  ? 'Create a program to group cultivars under a shared royalty / licensing scheme.'
                  : 'Try a different search term.'}
              </p>
              {programs.length === 0 && (
                <Button variant="primary" onClick={() => setCreating(true)}>
                  <Plus className="h-4 w-4" />
                  Create first program
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-3">
            {filtered.map((p) => {
              const typeLabel =
                p.program_type === 'other' && p.custom_type_label
                  ? p.custom_type_label
                  : PROGRAM_TYPE_LABELS[p.program_type];
              return (
                <Card key={p.id} className="hover:shadow-sm transition-shadow">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-neutral-900">{p.name}</h3>
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary-700 border border-primary-200">
                            {typeLabel}
                          </span>
                          {p.active ? (
                            <span className="inline-flex items-center gap-1 text-xs text-success-700">
                              <CheckCircle2 className="h-3.5 w-3.5" />
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs text-neutral-500">
                              <PauseCircle className="h-3.5 w-3.5" />
                              Inactive
                            </span>
                          )}
                          {typeof p.cultivar_count === 'number' && (
                            <span className="text-xs text-neutral-500">
                              · {p.cultivar_count} cultivar{p.cultivar_count === 1 ? '' : 's'}
                            </span>
                          )}
                        </div>

                        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-neutral-600">
                          {p.organisation_name && (
                            <span className="inline-flex items-center gap-1.5">
                              <Building2 className="h-3.5 w-3.5 text-neutral-400" />
                              {p.organisation_name}
                            </span>
                          )}
                          {p.royalty_rate != null && (
                            <span className="inline-flex items-center gap-1.5">
                              <DollarSign className="h-3.5 w-3.5 text-neutral-400" />
                              {formatRoyalty(p)}
                            </span>
                          )}
                          {p.reporting_cadence && (
                            <span className="inline-flex items-center gap-1.5">
                              <CalendarClock className="h-3.5 w-3.5 text-neutral-400" />
                              {REPORTING_CADENCE_LABELS[p.reporting_cadence]}
                              {formatDueDate(p) ? ` · due ${formatDueDate(p)}` : ''}
                            </span>
                          )}
                        </div>

                        {p.description && (
                          <p className="mt-2 text-sm text-neutral-500 line-clamp-2">{p.description}</p>
                        )}
                      </div>

                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openManageModal(p)}
                          title="Manage cultivars"
                        >
                          <Leaf className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/cultivar/admin/programs/${p.id}/price-versions`)}
                          title="Price versions"
                        >
                          <History className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/cultivar/admin/programs/${p.id}/contract-templates`)}
                          title="Contract templates"
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/cultivar/admin/programs/${p.id}/approvers`)}
                          title="Approvers"
                        >
                          <ShieldCheck className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditing(p)}
                          title="Edit"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteTarget(p)}
                          title="Delete"
                          className="text-error-600 hover:bg-error-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Create / edit modal */}
      {(creating || editing) && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
              <h2 className="text-lg font-semibold text-neutral-900">
                {editing ? `Edit program: ${editing.name}` : 'Create new program'}
              </h2>
              <button
                onClick={() => {
                  setCreating(false);
                  setEditing(null);
                }}
                className="text-neutral-400 hover:text-neutral-600"
                disabled={submitting}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6">
              <ProgramForm
                initialData={editing || undefined}
                defaultOrganisationId={!editing ? (user?.organisation_id ?? undefined) : undefined}
                lockOrganisation={!editing}
                onSubmit={editing ? handleUpdate : handleCreate}
                onCancel={() => {
                  setCreating(false);
                  setEditing(null);
                }}
                isSubmitting={submitting}
              />
            </div>
          </div>
        </div>
      )}

      {/* Manage cultivars modal */}
      {managingProgram && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
              <div>
                <h2 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
                  <Leaf className="h-5 w-5 text-primary-600" />
                  Cultivars — {managingProgram.name}
                </h2>
                <p className="text-sm text-neutral-500 mt-0.5">
                  Assign or remove cultivars from this program.
                </p>
              </div>
              <button
                onClick={closeManageModal}
                className="text-neutral-400 hover:text-neutral-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Add cultivar search */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-neutral-700">
                  Add cultivar to this program
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={cultivarSearch}
                    onChange={(e) => {
                      const q = e.target.value;
                      setCultivarSearch(q);
                      setShowCultivarDropdown(true);
                      if (cultivarSearchRef.current) clearTimeout(cultivarSearchRef.current);
                      cultivarSearchRef.current = setTimeout(() => searchCultivarsForAssign(q), 300);
                    }}
                    onFocus={() => {
                      if (cultivarSearch.trim().length >= 2) setShowCultivarDropdown(true);
                    }}
                    onBlur={() => setTimeout(() => setShowCultivarDropdown(false), 200)}
                    placeholder="Search cultivar name..."
                    className="input-professional w-full pl-10 pr-4"
                  />
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">
                    {searchingCultivars ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Search className="h-4 w-4" />
                    )}
                  </div>

                  {showCultivarDropdown && cultivarResults.length > 0 && (
                    <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
                      {cultivarResults.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          className="w-full text-left px-4 py-2.5 hover:bg-neutral-50 border-b border-neutral-100 last:border-b-0 flex items-center justify-between"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleAssignCultivar(c);
                          }}
                        >
                          <div>
                            <p className="text-sm font-medium text-neutral-900">{c.name}</p>
                            {c.trade_name && (
                              <p className="text-xs text-neutral-500">{c.trade_name}</p>
                            )}
                          </div>
                          {assigningId === c.id ? (
                            <Loader2 className="h-4 w-4 animate-spin text-primary-500" />
                          ) : (
                            <UserPlus className="h-4 w-4 text-primary-500" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}

                  {showCultivarDropdown && cultivarSearch.trim().length >= 2 && !searchingCultivars && cultivarResults.length === 0 && (
                    <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg px-4 py-3 text-sm text-neutral-500">
                      No unassigned cultivars found.
                    </div>
                  )}
                </div>
              </div>

              {/* Assigned cultivars list */}
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-neutral-700">
                  Assigned cultivars
                  {assignedCultivars.length > 0 && (
                    <span className="ml-2 text-xs font-normal text-neutral-500">
                      ({assignedCultivars.length})
                    </span>
                  )}
                </h3>

                {loadingAssigned ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
                  </div>
                ) : assignedCultivars.length === 0 ? (
                  <div className="rounded-lg border-2 border-dashed border-neutral-200 p-6 text-center">
                    <Leaf className="h-8 w-8 mx-auto text-neutral-300 mb-2" />
                    <p className="text-sm text-neutral-500">No cultivars assigned yet.</p>
                    <p className="text-xs text-neutral-400 mt-1">
                      Search for a cultivar above to assign it to this program.
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-neutral-100 rounded-lg border border-neutral-200">
                    {assignedCultivars.map((c) => (
                      <div key={c.id} className="flex items-center justify-between px-4 py-3">
                        <div>
                          <p className="text-sm font-medium text-neutral-900">{c.name}</p>
                          {c.trade_name && (
                            <p className="text-xs text-neutral-500">{c.trade_name}</p>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveCultivar(c)}
                          disabled={removingId === c.id}
                          className="text-neutral-400 hover:text-error-600 transition-colors disabled:opacity-50"
                          title="Remove from program"
                        >
                          {removingId === c.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <X className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="px-6 py-4 border-t border-neutral-200 flex justify-end">
              <Button variant="outline" onClick={closeManageModal}>
                Done
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">Delete program?</h3>
            <p className="text-sm text-neutral-600 mb-4">
              <span className="font-medium">{deleteTarget.name}</span> will be removed. Cultivars
              currently assigned to this program will be detached (not deleted). This cannot be
              undone.
            </p>
            {typeof deleteTarget.cultivar_count === 'number' && deleteTarget.cultivar_count > 0 && (
              <div className="rounded-lg p-3 bg-warning-50 border border-warning-200 mb-4">
                <p className="text-sm text-warning-800">
                  {deleteTarget.cultivar_count} cultivar
                  {deleteTarget.cultivar_count === 1 ? ' is' : 's are'} assigned to this program. The
                  server will block deletion until you reassign or detach them.
                </p>
              </div>
            )}
            <div className="flex items-center justify-end gap-3">
              <Button
                variant="ghost"
                onClick={() => setDeleteTarget(null)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleDelete}
                isLoading={submitting}
                className="bg-error-600 hover:bg-error-700"
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProgramsAdmin;
